import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Pencil, Phone, Wrench, HardHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function VendorsPanel({ readOnly, canDelete }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [trade, setTrade] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendors");
      setVendors(data);
    } catch {
      toast.error("Could not load vendors");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null); setName(""); setTrade(""); setPhone(""); setOpen(true);
  };
  const openEdit = (v) => {
    setEditing(v); setName(v.name); setTrade(v.trade || ""); setPhone(v.phone || ""); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Name must be at least 2 characters");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/vendors/${editing.id}`, { name: name.trim(), trade: trade.trim(), phone: phone.trim() });
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", { name: name.trim(), trade: trade.trim(), phone: phone.trim() });
        toast.success("Vendor added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!confirm(`Delete vendor ${v.name}? Tickets already assigned to them keep the record.`)) return;
    try { await api.delete(`/vendors/${v.id}`); toast.success("Vendor deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200">
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div className="text-sm font-semibold text-slate-800">
          {vendors.length} vendor{vendors.length !== 1 ? "s" : ""}
        </div>
        {!readOnly && (
          <Button data-testid="add-vendor-btn" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9">
            <Plus className="w-4 h-4 mr-1.5" /> Add vendor
          </Button>
        )}
      </div>
      {loading ? (
        <div className="p-8 grid place-items-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : vendors.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">
          No vendors or contractors yet. Add your first plumber, electrician or handyman above.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {vendors.map((v) => (
            <li key={v.id} data-testid={`vendor-row-${v.id}`} className="p-4 flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900 text-sm inline-flex items-center gap-1.5">
                    <HardHat className="w-3.5 h-3.5 text-slate-500" /> {v.name}
                  </span>
                  {v.trade && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 text-[10px] font-semibold">
                      <Wrench className="w-3 h-3" /> {v.trade}
                    </span>
                  )}
                </div>
                {v.phone && (
                  <a href={`tel:${v.phone}`} className="text-xs text-slate-600 mt-1 inline-flex items-center gap-1 hover:text-indigo-600">
                    <Phone className="w-3 h-3" /> {v.phone}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {!readOnly && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                )}
                {canDelete && (
                  <Button variant="ghost" size="sm" onClick={() => remove(v)} data-testid={`del-vendor-${v.id}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit vendor" : "New vendor"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="vendor-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. Ravi Electricals" />
            </div>
            <div>
              <Label>Trade / Specialty</Label>
              <Input data-testid="vendor-trade" value={trade} onChange={(e) => setTrade(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. Electrician, Plumber, AC Repair" />
            </div>
            <div>
              <Label>Phone number</Label>
              <Input data-testid="vendor-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-11" placeholder="+91 98xxxxxxxx" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="vendor-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Add vendor"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
